import React, { useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { Input, Tooltip } from 'antd';
import { QuestionCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';

interface IniSection {
  name: string;
  comment: string;
  entries: { key: string; value: string }[];
}

export interface IniFormViewRef {
  getContent: () => string;
}

function parseIni(content: string): IniSection[] {
  const sections: IniSection[] = [];
  let current: IniSection | null = null;
  let headerComment = '';

  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    const sectionMatch = trimmed.match(/^\[(.+)\]$/);
    if (sectionMatch) {
      current = { name: sectionMatch[1], comment: '', entries: [] };
      sections.push(current);
      headerComment = '';
      continue;
    }
    if (trimmed.startsWith('#') || trimmed.startsWith(';')) {
      const commentText = trimmed.replace(/^[#;]\s?/, '');
      if (current && current.entries.length === 0) {
        current.comment = current.comment ? current.comment + '\n' + commentText : commentText;
      } else {
        headerComment = headerComment ? headerComment + '\n' + commentText : commentText;
      }
      continue;
    }
    const kvMatch = trimmed.match(/^([^=]+?)=(.*)$/);
    if (kvMatch && current) {
      current.entries.push({ key: kvMatch[1].trim(), value: kvMatch[2].trim() });
    }
    if (kvMatch && !current) {
      current = { name: '默认', comment: headerComment, entries: [] };
      headerComment = '';
      sections.push(current);
      current.entries.push({ key: kvMatch[1].trim(), value: kvMatch[2].trim() });
    }
  }
  return sections;
}

function serializeIni(sections: IniSection[]): string {
  return sections.map((s) => {
    let block = `[${s.name}]\n`;
    if (s.comment) block += s.comment.split('\n').map((l) => `# ${l}`).join('\n') + '\n';
    block += s.entries.map((e) => `${e.key}=${e.value}`).join('\n');
    return block;
  }).join('\n\n') + '\n';
}

// Parse comment as key label map: "# xiaozhi=小智KEY;paa=小智密码" -> {xiaozhi: "小智KEY", paa: "小智密码"}
function parseKeyLabels(comment: string): Record<string, string> {
  const map: Record<string, string> = {};
  if (!comment) return map;
  for (const line of comment.split('\n')) {
    for (const part of line.split(';')) {
      const m = part.trim().match(/^([^=]+?)=(.+)$/);
      if (m) map[m[1].trim()] = m[2].trim();
    }
  }
  return map;
}

const IniFormView = forwardRef<IniFormViewRef, { content: string; onViewDoc?: (sectionName: string) => void }>(({ content, onViewDoc }, ref) => {
  const [sections, setSections] = useState<IniSection[]>([]);

  useEffect(() => {
    setSections(parseIni(content));
  }, [content]);

  useImperativeHandle(ref, () => ({
    getContent: () => serializeIni(sections),
  }));

  const handleChange = (sectionIdx: number, entryIdx: number, value: string) => {
    setSections((prev) => prev.map((s, si) => si === sectionIdx
      ? { ...s, entries: s.entries.map((e, ei) => ei === entryIdx ? { ...e, value } : e) }
      : s));
  };

  if (sections.length === 0) {
    return (
      <div style={{ padding: '16px 24px', color: 'var(--apple-text-secondary)', fontSize: 12, lineHeight: 2 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>暂无内容，可点击「导入配置」或切换到编辑模式手动填写。</div>
        <div style={{ opacity: 0.7 }}>格式规范：</div>
        <pre style={{ margin: '4px 0 0', fontSize: 12, fontFamily: 'monospace', background: 'var(--apple-bg-secondary, #f6f8fa)', borderRadius: 6, padding: '8px 12px', lineHeight: 1.8, color: 'inherit' }}>{`[功能名称]\n# key=标签名称;key2=标签名称2\nkey=value\nkey2=value2`}</pre>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {sections.map((section, si) => (
        <div key={si} style={{ border: '1px solid var(--ini-border, #e8e8e8)', borderRadius: 10, overflow: 'hidden' }}>
          {/* 标题栏 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid var(--ini-border, #e8e8e8)', background: 'var(--apple-bg-secondary, #fafafa)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 }}>
              {section.name}
              {onViewDoc && (
                <Tooltip title="查看配置说明">
                  <QuestionCircleOutlined
                    style={{ color: 'var(--apple-text-secondary, #999)', fontSize: 13, cursor: 'pointer' }}
                    onClick={() => onViewDoc(section.name)}
                  />
                </Tooltip>
              )}
            </div>
            <MinusCircleOutlined
              style={{ color: '#ff4d4f', fontSize: 14, cursor: 'pointer' }}
              onClick={() => setSections((prev) => prev.filter((_, i) => i !== si))}
            />
          </div>
          {/* 内容区 */}
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {section.entries.map((entry, ei) => {
              const labels = parseKeyLabels(section.comment);
              return (
                <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ minWidth: 140, fontSize: 13, color: 'var(--apple-text-secondary, #666)' }}>{labels[entry.key] || entry.key}</span>
                  <Input
                    value={entry.value}
                    onChange={(e) => handleChange(si, ei, e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
});

export default IniFormView;
